// @ts-check
/**
 * Fyzická mriežka klávesnice a rozloženia znakov.
 *
 * DÔLEŽITÉ ROZHODNUTIE:
 * Fyzická mriežka (poradie kláves, ich šírka a prst) je na QWERTZ/QWERTY klávesnici
 * rovnaká pre všetky jazyky. Preto výučba prstov NIKDY nezávisí od správnosti
 * jazykových popisov nižšie.
 *
 * Samotná správnosť písania sa NIKDY neodvodzuje z tohto súboru — engine porovnáva
 * znak, ktorý dodal operačný systém (`event.key`). Ak by teda bol niektorý popis
 * klávesy nepresný, lekcie a meranie fungujú ďalej; nesprávne by bol len nápis
 * na vizuálnej klávesnici.
 *
 * Popisy sú preto "best effort" a aplikácia ich dokáže opraviť dvoma spôsobmi:
 *   1. pasívne — každé stlačenie klávesy počas cvičenia aktualizuje mapu code → znak,
 *   2. aktívne — obrazovka "Kalibrácia", kde používateľ postupne stlačí klávesy.
 */

/** @typedef {'l-pinky'|'l-ring'|'l-middle'|'l-index'|'thumb'|'r-index'|'r-middle'|'r-ring'|'r-pinky'} FingerId */

/** @type {Record<FingerId, string>} */
export const FINGER_LABELS = {
  'l-pinky': 'ľavá malíčková',
  'l-ring': 'ľavá prstenníková',
  'l-middle': 'ľavá prostredníková',
  'l-index': 'ľavá ukazováková',
  thumb: 'palec',
  'r-index': 'pravá ukazováková',
  'r-middle': 'pravá prostredníková',
  'r-ring': 'pravá prstenníková',
  'r-pinky': 'pravá malíčková',
};

/** Skupina prsta pre farbenie (4 farby na ruku). */
/** @type {Record<FingerId, number>} */
export const FINGER_GROUP = {
  'l-pinky': 1, 'l-ring': 2, 'l-middle': 3, 'l-index': 4, thumb: 0,
  'r-index': 4, 'r-middle': 3, 'r-ring': 2, 'r-pinky': 1,
};

/**
 * @typedef {Object} KeyDef
 * @property {string} code   fyzická pozícia (KeyboardEvent.code)
 * @property {number} width  šírka v jednotkách klávesy
 * @property {FingerId} finger
 */

/**
 * Fyzická mriežka (US/QWERTZ ANSI: 13 / 13+Enter / 12 / 10+Shift). `null` = medzera
 * vyplnená kvôli zarovnaniu (napr. medzera za Enterom v druhom rade).
 * @type {(KeyDef|null)[][]}
 */
export const PHYSICAL_ROWS = [
  [
    { code: 'Backquote', width: 1, finger: 'l-pinky' },
    { code: 'Digit1', width: 1, finger: 'l-pinky' },
    { code: 'Digit2', width: 1, finger: 'l-ring' },
    { code: 'Digit3', width: 1, finger: 'l-middle' },
    { code: 'Digit4', width: 1, finger: 'l-index' },
    { code: 'Digit5', width: 1, finger: 'l-index' },
    { code: 'Digit6', width: 1, finger: 'r-index' },
    { code: 'Digit7', width: 1, finger: 'r-index' },
    { code: 'Digit8', width: 1, finger: 'r-middle' },
    { code: 'Digit9', width: 1, finger: 'r-ring' },
    { code: 'Digit0', width: 1, finger: 'r-pinky' },
    { code: 'Minus', width: 1, finger: 'r-pinky' },
    { code: 'Equal', width: 1, finger: 'r-pinky' },
    { code: 'Backspace', width: 2, finger: 'r-pinky' },
  ],
  [
    { code: 'Tab', width: 1.5, finger: 'l-pinky' },
    { code: 'KeyQ', width: 1, finger: 'l-pinky' },
    { code: 'KeyW', width: 1, finger: 'l-ring' },
    { code: 'KeyE', width: 1, finger: 'l-middle' },
    { code: 'KeyR', width: 1, finger: 'l-index' },
    { code: 'KeyT', width: 1, finger: 'l-index' },
    { code: 'KeyY', width: 1, finger: 'r-index' },
    { code: 'KeyU', width: 1, finger: 'r-index' },
    { code: 'KeyI', width: 1, finger: 'r-middle' },
    { code: 'KeyO', width: 1, finger: 'r-ring' },
    { code: 'KeyP', width: 1, finger: 'r-pinky' },
    { code: 'BracketLeft', width: 1, finger: 'r-pinky' },
    { code: 'BracketRight', width: 1, finger: 'r-pinky' },
    { code: 'Backslash', width: 1.5, finger: 'r-pinky' },
  ],
  [
    { code: 'CapsLock', width: 1.75, finger: 'l-pinky' },
    { code: 'KeyA', width: 1, finger: 'l-pinky' },
    { code: 'KeyS', width: 1, finger: 'l-ring' },
    { code: 'KeyD', width: 1, finger: 'l-middle' },
    { code: 'KeyF', width: 1, finger: 'l-index' },
    { code: 'KeyG', width: 1, finger: 'l-index' },
    { code: 'KeyH', width: 1, finger: 'r-index' },
    { code: 'KeyJ', width: 1, finger: 'r-index' },
    { code: 'KeyK', width: 1, finger: 'r-middle' },
    { code: 'KeyL', width: 1, finger: 'r-ring' },
    { code: 'Semicolon', width: 1, finger: 'r-pinky' },
    { code: 'Quote', width: 1, finger: 'r-pinky' },
    { code: 'Enter', width: 2.25, finger: 'r-pinky' },
  ],
  [
    { code: 'ShiftLeft', width: 2.25, finger: 'l-pinky' },
    { code: 'KeyZ', width: 1, finger: 'l-pinky' },
    { code: 'KeyX', width: 1, finger: 'l-ring' },
    { code: 'KeyC', width: 1, finger: 'l-middle' },
    { code: 'KeyV', width: 1, finger: 'l-index' },
    { code: 'KeyB', width: 1, finger: 'l-index' },
    { code: 'KeyN', width: 1, finger: 'r-index' },
    { code: 'KeyM', width: 1, finger: 'r-index' },
    { code: 'Comma', width: 1, finger: 'r-middle' },
    { code: 'Period', width: 1, finger: 'r-ring' },
    { code: 'Slash', width: 1, finger: 'r-pinky' },
    { code: 'ShiftRight', width: 2.75, finger: 'r-pinky' },
  ],
  [
    { code: 'Space', width: 6.25, finger: 'thumb' },
  ],
];

/** Domovské klávesy (na nich spočívajú prsty). */
export const HOME_KEYS = ['KeyA', 'KeyS', 'KeyD', 'KeyF', 'KeyJ', 'KeyK', 'KeyL', 'Semicolon'];

/**
 * Rozloženia znakov. `labels` mapuje code → [bez Shiftu, so Shiftom].
 * `confidence` označuje, ako veľmi sa dá popisom veriť (kalibrácia to opraví).
 *
 * @typedef {Object} Layout
 * @property {string} id
 * @property {string} name
 * @property {'high'|'medium'} confidence
 * @property {string} note
 * @property {Record<string, [string, string]>} labels
 */

/** Anglické QWERTY — popisy overené, žiadne pochybnosti. */
/** @type {Record<string, [string, string]>} */
const EN_QWERTY_LABELS = {
  Backquote: ['`', '~'], Digit1: ['1', '!'], Digit2: ['2', '@'], Digit3: ['3', '#'],
  Digit4: ['4', '$'], Digit5: ['5', '%'], Digit6: ['6', '^'], Digit7: ['7', '&'],
  Digit8: ['8', '*'], Digit9: ['9', '('], Digit0: ['0', ')'], Minus: ['-', '_'],
  Equal: ['=', '+'],
  KeyQ: ['q', 'Q'], KeyW: ['w', 'W'], KeyE: ['e', 'E'], KeyR: ['r', 'R'], KeyT: ['t', 'T'],
  KeyY: ['y', 'Y'], KeyU: ['u', 'U'], KeyI: ['i', 'I'], KeyO: ['o', 'O'], KeyP: ['p', 'P'],
  BracketLeft: ['[', '{'], BracketRight: [']', '}'], Backslash: ['\\', '|'],
  KeyA: ['a', 'A'], KeyS: ['s', 'S'], KeyD: ['d', 'D'], KeyF: ['f', 'F'], KeyG: ['g', 'G'],
  KeyH: ['h', 'H'], KeyJ: ['j', 'J'], KeyK: ['k', 'K'], KeyL: ['l', 'L'],
  Semicolon: [';', ':'], Quote: ['\'', '"'],
  KeyZ: ['z', 'Z'], KeyX: ['x', 'X'], KeyC: ['c', 'C'], KeyV: ['v', 'V'], KeyB: ['b', 'B'],
  KeyN: ['n', 'N'], KeyM: ['m', 'M'], Comma: [',', '<'], Period: ['.', '>'], Slash: ['/', '?'],
  Space: [' ', ' '],
};

/**
 * Slovenské QWERTZ. Typický znak tohto rozloženia: na hornom rade sú BEZ Shiftu
 * písmená s diakritikou a číslice sa píšu so Shiftom.
 *
 * POZOR: niektoré okrajové popisy (`§`, `ň`, `ô`, mŕtve klávesy na `Equal`) sa
 * medzi verziami Windows a distribúciami líšia. Preto `confidence: 'medium'`
 * a aplikácia ich vie opraviť kalibráciou. Na správnosť cvičení to nemá vplyv.
 */
/** @type {Record<string, [string, string]>} */
const SK_QWERTZ_LABELS = {
  Backquote: [';', '°'], Digit1: ['+', '1'], Digit2: ['ľ', '2'], Digit3: ['š', '3'],
  Digit4: ['č', '4'], Digit5: ['ť', '5'], Digit6: ['ž', '6'], Digit7: ['ý', '7'],
  Digit8: ['á', '8'], Digit9: ['í', '9'], Digit0: ['é', '0'], Minus: ['=', '%'],
  Equal: ['´', 'ˇ'],
  KeyQ: ['q', 'Q'], KeyW: ['w', 'W'], KeyE: ['e', 'E'], KeyR: ['r', 'R'], KeyT: ['t', 'T'],
  KeyU: ['u', 'U'], KeyI: ['i', 'I'], KeyO: ['o', 'O'], KeyP: ['p', 'P'],
  BracketLeft: ['ú', '/'], BracketRight: ['ä', '('], Backslash: ['ň', ')'],
  KeyA: ['a', 'A'], KeyS: ['s', 'S'], KeyD: ['d', 'D'], KeyF: ['f', 'F'], KeyG: ['g', 'G'],
  KeyH: ['h', 'H'], KeyJ: ['j', 'J'], KeyK: ['k', 'K'], KeyL: ['l', 'L'],
  Semicolon: ['ô', '"'], Quote: ['§', '!'],
  KeyZ: ['y', 'Y'], KeyY: ['z', 'Z'], KeyX: ['x', 'X'], KeyC: ['c', 'C'], KeyV: ['v', 'V'],
  KeyB: ['b', 'B'],
  KeyN: ['n', 'N'], KeyM: ['m', 'M'], Comma: [',', '?'], Period: ['.', ':'], Slash: ['-', '_'],
  Space: [' ', ' '],
};

/**
 * Slovenské QWERTY — čistý anglický layout s pridanou slovenskou diakritikou
 * na AltGr. Pre výučbu je vhodnejšie ako QWERTZ (číslice bez Shiftu).
 */
/** @type {Record<string, [string, string]>} */
const SK_QWERTY_LABELS = {
  ...EN_QWERTY_LABELS,
  BracketLeft: ['ú', '/'], BracketRight: ['ä', '('],
  Semicolon: ['ô', '"'], Quote: ['§', '!'],
};

/**
 * České QWERTZ — sesterské k slovenskému, rovnaká logika horného radu.
 * `confidence: 'medium'` z rovnakého dôvodu ako pri SK.
 */
/** @type {Record<string, [string, string]>} */
const CS_QWERTZ_LABELS = {
  Backquote: [';', '°'], Digit1: ['+', '1'], Digit2: ['ě', '2'], Digit3: ['š', '3'],
  Digit4: ['č', '4'], Digit5: ['ř', '5'], Digit6: ['ž', '6'], Digit7: ['ý', '7'],
  Digit8: ['á', '8'], Digit9: ['í', '9'], Digit0: ['é', '0'], Minus: ['=', '%'],
  Equal: ['´', 'ˇ'],
  KeyQ: ['q', 'Q'], KeyW: ['w', 'W'], KeyE: ['e', 'E'], KeyR: ['r', 'R'], KeyT: ['t', 'T'],
  KeyU: ['u', 'U'], KeyI: ['i', 'I'], KeyO: ['o', 'O'], KeyP: ['p', 'P'],
  BracketLeft: ['ú', '/'], BracketRight: [')', '('], Backslash: ['¨', '!'],
  KeyA: ['a', 'A'], KeyS: ['s', 'S'], KeyD: ['d', 'D'], KeyF: ['f', 'F'], KeyG: ['g', 'G'],
  KeyH: ['h', 'H'], KeyJ: ['j', 'J'], KeyK: ['k', 'K'], KeyL: ['l', 'L'],
  Semicolon: ['ů', '"'], Quote: ['§', '!'],
  KeyZ: ['y', 'Y'], KeyY: ['z', 'Z'], KeyX: ['x', 'X'], KeyC: ['c', 'C'], KeyV: ['v', 'V'],
  KeyB: ['b', 'B'],
  KeyN: ['n', 'N'], KeyM: ['m', 'M'], Comma: [',', '?'], Period: ['.', ':'], Slash: ['-', '_'],
  Space: [' ', ' '],
};

/** @type {Record<string, Layout>} */
export const LAYOUTS = {
  'sk-qwertz': {
    id: 'sk-qwertz',
    name: 'Slovenská QWERTZ',
    confidence: 'medium',
    note: 'Číslice sa píšu so Shiftom, na hornom rade sú bez Shiftu písmená s diakritikou.',
    labels: SK_QWERTZ_LABELS,
  },
  'sk-qwerty': {
    id: 'sk-qwerty',
    name: 'Slovenská QWERTY (US + diakritika)',
    confidence: 'medium',
    note: 'Anglické rozloženie doplnené o slovenskú diakritiku; číslice bez Shiftu.',
    labels: SK_QWERTY_LABELS,
  },
  'cs-qwertz': {
    id: 'cs-qwertz',
    name: 'Česká QWERTZ',
    confidence: 'medium',
    note: 'Rovnaká logika horného radu ako slovenská QWERTZ.',
    labels: CS_QWERTZ_LABELS,
  },
  'en-qwerty': {
    id: 'en-qwerty',
    name: 'Anglická QWERTY',
    confidence: 'high',
    note: 'Referenčné rozloženie, popisy kláves sú overené.',
    labels: EN_QWERTY_LABELS,
  },
};

export const DEFAULT_LAYOUT_ID = 'sk-qwertz';

/**
 * @param {string} id
 * @returns {Layout}
 */
export function getLayout(id) {
  return LAYOUTS[id] ?? LAYOUTS[DEFAULT_LAYOUT_ID];
}

/**
 * Zlúči predvolené popisy s používateľskou kalibráciou.
 * @param {string} id
 * @param {Record<string, string>} [overrides]  code → znak (bez Shiftu)
 * @returns {Layout}
 */
export function resolveLayout(id, overrides) {
  const base = getLayout(id);
  if (!overrides || Object.keys(overrides).length === 0) return base;
  /** @type {Record<string, [string, string]>} */
  const labels = { ...base.labels };
  for (const [code, ch] of Object.entries(overrides)) {
    const existing = labels[code];
    if (!existing) continue;
    labels[code] = [ch, existing[1]];
  }
  return { ...base, labels };
}

/** Fyzická pozícia → prst. */
/** @type {Map<string, FingerId>} */
export const CODE_TO_FINGER = new Map();
/** Fyzická pozícia → šírka. */
/** @type {Map<string, number>} */
export const CODE_TO_WIDTH = new Map();

for (const row of PHYSICAL_ROWS) {
  for (const key of row) {
    if (!key) continue;
    CODE_TO_FINGER.set(key.code, key.finger);
    CODE_TO_WIDTH.set(key.code, key.width);
  }
}

/**
 * Nájde fyzickú pozíciu pre daný znak v rozložení (na zvýraznenie "ďalšej klávesy").
 * Vracia `null`, ak znak nie je na klávesnici (napr. Enter, alebo neznámy znak).
 * @param {Layout} layout
 * @param {string} ch
 * @returns {{code: string, shift: boolean}|null}
 */
export function findKeyForChar(layout, ch) {
  if (ch === '\n') return { code: 'Enter', shift: false };
  for (const [code, pair] of Object.entries(layout.labels)) {
    if (pair[0] === ch) return { code, shift: false };
    if (pair[1] === ch && pair[1] !== pair[0]) return { code, shift: true };
  }
  return null;
}
